/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    bool, func, object, string,
} from 'prop-types';
import { Button, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Header from './Partials/Header';
import { getIsProductFilterZipcodeValid, getUserSubmittedProductFilterZipcode, getValidatedZipcode } from '../../../../state/ducks/App/App-Selectors';
import {
    clearUserSubmittedProductFilterZipcode, clearValidatedZipcode, resetProductFilterZipcodeValidity, setUserSubmittedProductFilterZipcode, validateProductFilterZipcode,
} from '../../../../state/ducks/App/App-Actions';
import Overlay from './Partials/Overlay';
import { isEmpty } from '../../../pages/Account/utils/object';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
        '& .MuiDivider-root': {
            maxWidth: '1300px',
            margin: '0 auto',
        },
        '& .MuiButton-root': {
            color: theme.palette.white,
            textTransform: 'uppercase',
            fontSize: '13px',
            marginTop: '30px',
            padding: '5px 60px',
            fontWeight: '700',
            width: 'fit-content',
            backgroundColor: theme.palette.colorPrimary,
            '-webkit-font-smoothing': 'antialiased',
            '&:hover': {
                backgroundColor: theme.palette.colorPrimary,
            },
        },
    },
    subContainer: {
        padding: '50px 0',
        '@media screen and (max-width: 768px)': {
            padding: '50px 20px',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: 'auto',
        '& input': {
            width: '100%',
            maxWidth: '330px',
            padding: '10px',
            textAlign: 'center',
            textTransform: 'uppercase',
            outline: 'none',
        },
        '& .validZipCode': {
            border: '1px solid green',
        },
        '& .invalidZipCode': {
            border: '1px solid red',
        },
    },
    error: {
        fontSize: '12px',
        marginTop: '5px',
        textAlign: 'center',
        color: 'red !important',
    },
}));

const isCanadaZip = (zipcode) => new RegExp(/^[a-zA-Z]{1}[0-9]{1}[a-zA-Z]{1}[- ]{0,1}[0-9]{1}[a-zA-Z]{1}[0-9]{1}/).test(zipcode);

const RecipientAddress = ({
    valZip, clearValidatedZip, zipValid, resetZipValidity, validatedZip, clearFilterZip,
    userSubmittedZip, setFilterZip, selectedSku, data, scrollDownToSection, trackEvent, addToCartData, setAddToCartData,
}) => {
    const classes = useStyles();
    const heading = data?.title || '';
    const subHeading = data?.sub_title || '';

    const [zipField, setZipField] = useState('');
    const [hasError, setHasError] = useState(false);

    const validateZip = () => {
        // this function determines when to validate a zipcode that the user enters
        if (zipField.length >= 5) {
            // if user enters five chars check if valid zip
            valZip(zipField);
        } else if (zipField.length < 5 && validatedZip !== '') {
            // if there are fewer than 5 chars and there exists a zip in redux clear it
            // we do this so that if someone enters a zipcode, then deletes it the filtering goes away.
            clearValidatedZip(true);
        }
        if (zipField.length < 5 && userSubmittedZip !== '') {
            clearFilterZip();
        }
        if (zipField.length !== 5 && zipValid !== null) {
            // reset the zip is valid flag in redux if zip is not characters.
            resetZipValidity();
        }
    };

    useEffect(() => validateZip(), [zipField]);

    const convertZipCode = (zipcode, preState) => {
        if (preState.length < zipcode.length
            && zipcode.length >= 6
            && isCanadaZip(zipcode)) {
            const zipSplit = zipcode.replace(/\s/gi, '').toUpperCase().split('');
            zipSplit.splice(3, 0, ' ');
            return zipSplit.join('');
        }
        return zipcode;
    };

    const handleZipChange = (e) => {
        // empty the state cart data, if any
        if (!isEmpty(addToCartData)) setAddToCartData({});
        setHasError(false);
        let zipCode = e.target.value;
        const numericOnly = /^[0-9]+$/;
        if (zipCode && (Number.isNaN(Number(zipCode)) || !numericOnly.test(e.target.value))) {
            return;
        }
        if (isCanadaZip(zipCode)) {
            zipCode = convertZipCode(zipCode, zipField);
        }
        setZipField(zipCode);
    };

    const submitUserSelectedZipcode = () => {
        if (!zipValid) {
            setHasError(true);
            return;
        }
        if (validatedZip === zipField) {
            setFilterZip(zipField.trimEnd());
            scrollDownToSection('frequency_container');
        }
        trackEvent('Subscriptions Experience', 'click – Step 2', 'Continue');
    };

    return (
        <div className={classes.container} id="recipient_container">
            <div className={classes.subContainer}>
                <Header id="2" heading={heading} subHeading={subHeading} />
                <form className={classes.formContainer}>
                    <input
                        data-testid="zipcode-field"
                        placeholder="zip code"
                        onChange={handleZipChange}
                        className={`${zipField.length > 4 && (zipValid ? 'validZipCode' : 'invalidZipCode')}`}
                    />
                    {hasError && <div className={classes.error}>Please Enter Valid Zipcode.</div>}
                    <Button data-testid="continue-zipcode" onClick={submitUserSelectedZipcode}>Continue</Button>
                </form>
            </div>
            <Divider />
            {!selectedSku?.id && <Overlay />}
        </div>
    );
};

RecipientAddress.defaultProps = {
    zipValid: false,
};

RecipientAddress.propTypes = {
    zipValid: bool,
    valZip: func.isRequired,
    validatedZip: string.isRequired,
    clearFilterZip: func.isRequired,
    resetZipValidity: func.isRequired,
    clearValidatedZip: func.isRequired,
    userSubmittedZip: string.isRequired,
    setFilterZip: func.isRequired,
    selectedSku: object.isRequired,
    data: object.isRequired,
    trackEvent: func.isRequired,
    scrollDownToSection: func.isRequired,
    addToCartData: object.isRequired,
    setAddToCartData: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    valZip: bindActionCreators(validateProductFilterZipcode, dispatch),
    clearValidatedZip: bindActionCreators(clearValidatedZipcode, dispatch),
    clearFilterZip: bindActionCreators(clearUserSubmittedProductFilterZipcode, dispatch),
    resetZipValidity: bindActionCreators(resetProductFilterZipcodeValidity, dispatch),
    setFilterZip: bindActionCreators(setUserSubmittedProductFilterZipcode, dispatch),
});

const mapStateToProps = (state) => ({
    validatedZip: getValidatedZipcode(state),
    zipValid: getIsProductFilterZipcodeValid(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(RecipientAddress);
